/**
 * CART STATE MANAGER UTILITIES
 *
 * Centralized state management system for all cart-related operations in the Shopify theme.
 * Provides a single source of truth for cart data, manages real-time updates, and coordinates
 * communication between all cart components through a comprehensive event system.
 *
 * **DATA SOURCE**: All cart state is sourced exclusively from the `cart-json` Liquid section,
 * which provides enriched cart data with UI-specific properties. The standard Shopify Cart AJAX
 * API (`/cart.json`) is NOT used as the source of that enriched payload — ensuring consistency and
 * eliminating data synchronization issues for line-item display data.
 *
 * Used by: cart-drawer.js, cart-item.js, cart-items.js, cart-note.js, cart-discounts.js,
 *          free-shipping-banner.js, buy-more-save-more-banner.js, and all CartComponentBase extensions
 *
 * Key Features:
 * - Real-time cart state synchronization via cart-json section rendering
 * - Event-driven architecture for component communication
 * - Automatic cart count updates across all UI elements
 * - Enriched cart data with UI-specific properties (pricing, flags, messaging)
 * - Visibility change handling for state refresh
 * - Comprehensive error handling and retry mechanisms
 * - Memory-efficient caching and cleanup patterns
 * - Support for bulk operations and complex cart manipulations
 * - Bundled section rendering for optimal performance (fewer network requests)
 *
 * Integration Patterns:
 * - CartComponentBase classes: Access via `this.cartStateManager` getter
 * - External components: Import `addToCart` from `@utilities/theme`
 * - Global access: Available as `window.cartStateManager` and `window.theme.utilities.cart`
 * - Legacy support: `window.addToCart()` and `window.openCartDrawer()` functions
 *
 * Event System (via pub-sub — see utilities-events.js):
 * - cart-state:changed  (sticky)  Published when cart state changes
 * - cart-state:loaded   (sticky)  Published when initial state is loaded
 * - cart-state:refreshed          Published after visibility-triggered refreshes
 * - cart-state:initial-load (sticky)  Published once on first state load
 * - cart-state:item:added/removed/updated  Published for specific item operations
 * - cart-state:error              Published when operations fail
 * - cart-system:ready   (sticky)  Published when both state manager and drawer are ready
 *
 * Auto-initialization:
 * - Initializes automatically on DOMContentLoaded
 * - Sets up global references and theme integration
 * - Configures visibility change listeners for state synchronization
 *
 * @example
 * // Access from CartComponentBase subclass
 * const stateManager = this.cartStateManager;
 * await stateManager.addItems([{id: 123, quantity: 1}]);
 *
 * @example
 * // Access from external component
 * import { addToCart } from '@utilities/theme';
 * await addToCart({id: 123, quantity: 1});
 *
 * @example
 * // Listen for cart changes via pub-sub
 * import { subscribe } from '@utilities/events';
 * subscribe('cart-state:changed', (data) => {
 *   console.log('Cart updated:', data.state);
 * });
 *
 * @namespace CartStateManagerUtilities
 * @since 2024
 * @version 4.0.0
 */

import { publish, EVENTS } from '@utilities/events';

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function sanitizeCartAddItem(item) {
  const sanitized = {
    ...item,
    properties:
      item.properties && typeof item.properties === 'object'
        ? { ...item.properties }
        : {},
  };
  return sanitized;
}

/**
 * Cart State Manager Class
 * @class CartStateManager
 */
export class CartStateManager {
  constructor() {
    /** @type {Object} Current cart state - populated entirely from cart-json section */
    this.state = {
      note: "",
      attributes: {},
      currency: "USD",
      requiresShipping: false,
      renderCartNote: true,
      itemCount: 0,
      totalPrice: 0,
      totalPriceMoney: "$0",
      originalTotalPrice: 0,
      inclusionsOnlySubtotal: 0,
      inclusionsOnlySubtotalWithDiscounts: 0,
      inclusionsOnlySubtotalAmount: 0,
      inclusionsOnlySubtotalWithDiscountsAmount: 0,
      totalDiscount: 0,
      discountApplications: [],
      items: [],
      compareAtPriceTotals: {},
      discountTotals: {},
      finalDiscountTotals: {},
    };

    /** @type {boolean} Whether the state is currently being updated */
    this.isUpdating = false;

    /** @type {Map} Cache for product data */
    this.productCache = new Map();

    /** @type {boolean} Whether debug mode is enabled */
    this.debug = false;

    /** @type {boolean} Whether the initial state has been loaded */
    this.initialStateLoaded = false;

    /** @type {cart-drawer|null} Reference to active cart drawer */
    this.drawer = null;

    /** @type {Object|null} Pending changes when events are suppressed */
    this._pendingChanges = null;

    /** @type {Object|null} Previous state when events are suppressed */
    this._previousState = null;

  }

  /**
   * Checks if debug mode is enabled for this component
   * @returns {boolean} Whether debug logging is enabled
   */
  allowDebug() {
    return this.debug;
  }

  /**
   * Enable or disable debug mode
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    this.debug = !!enabled;
    console.log(
      `CartStateManager: Debug mode ${enabled ? "enabled" : "disabled"}`
    );
  }





  /**
   * Initialize the cart state manager
   */
  async initialize() {
    if (this.allowDebug()) {
      console.log("CartStateManager: Initializing cart state manager");
    }

    // Load initial cart state from Shopify
    await this.refreshState();

    // Set up visibility change listener
    this._setupVisibilityChangeListener();

    // Set up global references
    window.cartStateManager = this;

    // Set theme.utilities.cart reference for consistency with other utilities
    if (window.theme?.utilities) {
      window.theme.utilities.cart = this;

      // Debug logging for utilities readiness
      if (this.allowDebug()) {
        console.log("CartStateManager: Theme utilities available", {
          availableUtilities: Object.keys(window.theme.utilities),
          cartUtilitySet: !!window.theme.utilities.cart,
          drawerManagerAvailable: !!window.theme.utilities.cartDrawerManager
        });
      }

      // Set the drawer property to the single cart drawer on the page
      this.drawer = document.querySelector("cart-drawer");
      if (this.allowDebug()) {
        console.log("CartStateManager: Set drawer property", {
          hasDrawer: !!this.drawer,
          drawerId: this.drawer?.id || "none",
        });
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Added to window.theme.utilities.cart");
      }
    } else {
      console.warn(
        "CartStateManager: window.theme.utilities not available yet"
      );
    }

    this._publish(EVENTS.CART_STATE_LOADED, { state: this.state }, { sticky: true });

    // Check if cart system is ready now that state manager is initialized
    this._checkAndDispatchCartSystemReady();

    if (this.allowDebug()) {
      console.log("CartStateManager: Initialization complete", {
        itemCount: this.state.itemCount,
        totalPrice: this.state.totalPriceMoney,
        itemsSubtotal: this.state.inclusionsOnlySubtotal,
      });
    }
  }

  /**
   * Publish a cart event via the global pub-sub bus.
   * @param {string} eventName - Event name to publish
   * @param {Object} data - Payload delivered to subscribers
   * @param {Object} [options] - Publish options (e.g. { sticky: true })
   * @private
   */
  _publish(eventName, data, options) {
    if (this.allowDebug()) {
      console.log("CartStateManager: Publishing event", {
        type: eventName,
        data,
        sticky: !!options?.sticky,
      });
    }

    publish(eventName, data, options);
  }

  /**
   * Set up document visibility change listener
   * @private
   */
  _setupVisibilityChangeListener() {
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible') {
        if (this.allowDebug()) {
          console.log('CartStateManager: Document became visible, refreshing cart state');
        }

        try {
          await this.refreshState({
            source: 'visibility-change',
            force: true // Always force refresh on visibility change to ensure UI updates
          });

          this._publish(EVENTS.CART_STATE_REFRESHED, {
            state: this.state,
            reason: 'visibility-change',
            timestamp: Date.now()
          });

          if (this.allowDebug()) {
            console.log('CartStateManager: State refreshed due to visibility change', {
              itemCount: this.state.itemCount,
              totalPrice: this.state.totalPriceMoney
            });
          }
        } catch (error) {
          console.error('CartStateManager: Error refreshing state on visibility change:', error);
        }
      }
    });

    if (this.allowDebug()) {
      console.log('CartStateManager: Visibility change listener set up');
    }
  }

  /**
   * Check if cart drawer is ready and dispatch cart-system:ready event
   * @private
   */
  _checkAndDispatchCartSystemReady() {
    if (this.drawer) {
      this._publish(EVENTS.CART_SYSTEM_READY, {
        cartStateManager: this,
        cartDrawer: this.drawer,
      }, { sticky: true });

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart system fully ready - both state manager and cart drawer available"
        );
      }
    } else {
      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart state manager ready, waiting for cart drawer"
        );
      }
    }
  }

  /**
   * Refresh cart state from cart-json section
   * @param {Object} options - Options for the refresh
   * @param {boolean} options.force - Force UI update even if no changes detected
   * @param {string} options.source - Source of the refresh request for debugging
   */
  async refreshState(options = {}) {
    const { force = false, source = 'unknown' } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Refreshing cart state from cart-json section", {
        force,
        source
      });
    }

    try {
      // Fetch enriched cart data from cart-json section
      const enrichedCartData = await this.#fetchEnrichedCartData();

      if (!enrichedCartData) {
        throw new Error("Failed to fetch enriched cart data");
      }

      if (this.allowDebug()) {
        console.log("CartStateManager: Enriched cart data fetched", {
          itemCount: enrichedCartData.itemCount,
          totalPrice: enrichedCartData.totalPrice,
          totalDiscount: enrichedCartData.totalDiscount,
          itemsLength: enrichedCartData.items?.length || 0,
          source,
        });
      }

      // Update state with enriched data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { force, source, suppressEvents: true });

      // Now dispatch the state change event
      this.#dispatchStateChangeEvent(enrichedCartData, { force, source });

      return this.state;
    } catch (error) {
      console.error("CartStateManager: Error refreshing cart state:", error);
      throw error;
    }
  }

  /**
   * Update the internal state and optionally emit change event
   * @param {Object} newState - New cart state data from cart-json section
   * @param {Object} options - Options for the update
   * @param {boolean} options.force - Force event dispatch even if no changes detected
   * @param {string} options.source - Source of the update for debugging
   * @param {boolean} options.suppressEvents - Don't dispatch events (used when fetching enriched data first)
   * @private
   */
  _updateState(newState, options = {}) {
    const { force = false, source = 'unknown', suppressEvents = false } = options;
    const previousState = { ...this.state };

    // Directly use the enriched cart data (already in camelCase from cart-json section)
    this.state = {
      ...this.state,
      ...newState,
      items: Array.isArray(newState.items) ? newState.items : this.state.items,
    };

    // Store changes for later use if events are suppressed
    this._pendingChanges = this._getStateChanges(previousState, this.state);
    this._previousState = previousState;

    if (!suppressEvents) {
      this.#dispatchStateChangeEvent(newState, { force, source });
    }
  }

  /**
   * Dispatch state change event with current state and changes
   * @param {Object} originalNewState - The original new state data that triggered the update
   * @param {Object} options - Options for the event dispatch
   * @param {boolean} options.force - Force event dispatch even if no changes detected
   * @param {string} options.source - Source of the update for debugging
   * @private
   */
  #dispatchStateChangeEvent(originalNewState, options = {}) {
    const { force = false, source = 'unknown' } = options;
    const changes = this._pendingChanges || {};
    const previousState = this._previousState || {};
    const hasChanges = Object.keys(changes).length > 0;

    // Only emit state change event if there are actual changes or if forced
    if (hasChanges || force) {
      this._publish(EVENTS.CART_STATE_CHANGED, {
        state: this.state,
        previousState,
        changes: changes,
        source,
        forced: force && !hasChanges
      }, { sticky: true });

      // Auto-update all cart count elements if item count changed
      if (changes.itemCount) {
        this.updateAllCartCountElements();
      }
    }

    // Mark initial state as loaded
    if (!this.initialStateLoaded) {
      this.initialStateLoaded = true;
      this._publish(EVENTS.CART_STATE_INITIAL_LOAD, { state: this.state, source }, { sticky: true });
    }

    // Clear pending changes
    this._pendingChanges = null;
    this._previousState = null;

  }

  /**
   * Get the differences between two states
   * @param {Object} previousState - Previous state
   * @param {Object} currentState - Current state
   * @returns {Object} Object describing the changes
   * @private
   */
  _getStateChanges(previousState, currentState) {
    const changes = {};

    // Check for item count changes
    if (previousState.itemCount !== currentState.itemCount) {
      changes.itemCount = {
        from: previousState.itemCount,
        to: currentState.itemCount,
      };
    }

    // Check for total price changes
    if (previousState.totalPrice !== currentState.totalPrice) {
      changes.totalPrice = {
        from: previousState.totalPrice,
        to: currentState.totalPrice,
      };
    }

    // Check for total price changes
    if (previousState.totalPriceMoney !== currentState.totalPriceMoney) {
      changes.totalPriceMoney = {
        from: previousState.totalPriceMoney,
        to: currentState.totalPriceMoney,
      };
    }

    // Check for discount changes
    if (previousState.totalDiscount !== currentState.totalDiscount) {
      changes.totalDiscount = {
        from: previousState.totalDiscount,
        to: currentState.totalDiscount,
      };
    }

    // Check for discount applications changes
    const previousDiscounts = previousState.discountApplications || [];
    const currentDiscounts = currentState.discountApplications || [];
    if (
      JSON.stringify(previousDiscounts) !== JSON.stringify(currentDiscounts)
    ) {
      changes.discountApplications = {
        from: previousDiscounts,
        to: currentDiscounts,
      };

      // Determine what changed with discounts (using unique titles to avoid duplicates)
      const previousDiscountTitles = new Set(
        previousDiscounts.map((d) => d.title)
      );
      const currentDiscountTitles = new Set(
        currentDiscounts.map((d) => d.title)
      );

      if (this.allowDebug()) {
        console.log("CartStateManager: Analyzing discount changes", {
          previousTitles: [...previousDiscountTitles],
          currentTitles: [...currentDiscountTitles],
          previousCount: previousDiscounts.length,
          currentCount: currentDiscounts.length,
        });
      }

      // Check for added discounts
      const addedDiscounts = [...currentDiscountTitles].filter(
        (title) => !previousDiscountTitles.has(title)
      );
      if (addedDiscounts.length > 0) {
        changes.discountsAdded = addedDiscounts;
        if (this.allowDebug()) {
          console.log(
            "CartStateManager: Discounts added detected",
            addedDiscounts
          );
        }
      }

      // Check for removed discounts
      const removedDiscounts = [...previousDiscountTitles].filter(
        (title) => !currentDiscountTitles.has(title)
      );
      if (removedDiscounts.length > 0) {
        changes.discountsRemoved = removedDiscounts;
        if (this.allowDebug()) {
          console.log(
            "CartStateManager: Discounts removed detected",
            removedDiscounts
          );
        }
      }
    }

    // Check for items changes with detailed analysis
    const previousItems = previousState.items || [];
    const currentItems = currentState.items || [];

    if (JSON.stringify(previousItems) !== JSON.stringify(currentItems)) {
      changes.items = {
        from: previousItems,
        to: currentItems,
      };

      // Determine the type of items change
      const previousItemKeys = new Set(previousItems.map((item) => item.key));
      const currentItemKeys = new Set(currentItems.map((item) => item.key));

      // Check for added items
      const addedKeys = [...currentItemKeys].filter(
        (key) => !previousItemKeys.has(key)
      );
      if (addedKeys.length > 0) {
        changes.itemsAdded = addedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items added detected", addedKeys);
        }
      }

      // Check for removed items
      const removedKeys = [...previousItemKeys].filter(
        (key) => !currentItemKeys.has(key)
      );
      if (removedKeys.length > 0) {
        changes.itemsRemoved = removedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items removed detected", removedKeys);
        }
      }

      // Check for updated items (same key but different content)
      const commonKeys = [...currentItemKeys].filter((key) =>
        previousItemKeys.has(key)
      );
      const updatedKeys = commonKeys.filter((key) => {
        const prevItem = previousItems.find((item) => item.key === key);
        const currItem = currentItems.find((item) => item.key === key);
        return JSON.stringify(prevItem) !== JSON.stringify(currItem);
      });

      if (updatedKeys.length > 0) {
        changes.itemsUpdated = updatedKeys;
        if (this.allowDebug()) {
          console.log("CartStateManager: Items updated detected", updatedKeys);
        }
      }
    }

    return changes;
  }

  /**
   * Add item(s) to cart using Shopify Cart API
   *
   * Convenience method that accepts either a single item or array of items.
   * Automatically tracks the source component and dispatches relevant events.
   *
   * @param {Object|Array} items - Item or array of items to add to cart
   * @param {number} items.id - Variant ID of the product
   * @param {number} items.quantity - Quantity to add
   * @param {Object} [items.properties] - Line item properties
   * @param {boolean} [openDrawer=true] - Whether to open cart drawer after adding
   * @returns {Promise<Object>} Promise resolving to operation result
   * @throws {Error} If cart system is not ready or API call fails
   * @fires cart-state:item:adding - Before attempting to add items
   * @fires cart-state:item:added - After successfully adding items
   * @fires cart-state:changed - When cart state updates
   *
   * @example
   * // Add single item
   * await cartStateManager.addItem({
   *   id: 123456789,
   *   quantity: 1,
   *   properties: { gift_message: 'Happy Birthday!' }
   * });
   *
   * @example
   * // Add multiple items
   * await cartStateManager.addItem([
   *   { id: 123, quantity: 1 },
   *   { id: 456, quantity: 2 }
   * ], false); // Don't open drawer
   */
  async addItem(items, openDrawer = true) {
    // Proxy to addItems for consistency
    const itemsArray = Array.isArray(items) ? items : [items];
    return this.addItems(itemsArray, openDrawer);
  }

  /**
   * Add multiple items to cart using Shopify's add.js endpoint
   *
   * Core method for adding items to cart. Handles both single and bulk additions,
   * manages loading states, and ensures proper event dispatching throughout the process.
   * Automatically adds component tracking properties to items.
   *
   * @param {Array} items - Array of cart items to add
   * @param {number} items[].id - Variant ID of the product
   * @param {number} items[].quantity - Quantity to add
   * @param {Object} [items[].properties] - Line item properties
   * @param {boolean} [openDrawer=true] - Whether to open cart drawer after adding
   * @returns {Promise<Object>} Promise resolving to add result and derived line items
   * @throws {Error} If operation is blocked by concurrent update or API call fails
   * @fires cart-state:item:adding - Before attempting to add items
   * @fires cart-state:item:added - After successfully adding items
   * @fires cart-state:changed - When cart state updates
   * @fires cart-state:error - If addition fails
   *
   * @example
   * // Add membership bundle
   * await cartStateManager.addItems([
   *   { id: 123, quantity: 1, properties: { _gc_bundle_id: 'mens-membership' }},
   *   { id: 456, quantity: 1, properties: { _gc_bundle_id: 'mens-membership' }}
   * ]);
   */
  async addItems(items, openDrawer = true) {
    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Add items blocked - already updating");
      }
      return;
    }

    const itemsArray = Array.isArray(items) ? items : [items];
    const previousItems = Array.isArray(this.state.items) ? [...this.state.items] : [];

    itemsArray.forEach((item) => {
      if (!item.properties) {
        item.properties = {};
      }
      if (!item.properties.hasOwnProperty("_add_to_cart_component")) {
        item.properties["_add_to_cart_component"] = "unknown";
      }
    });

    const requestItems = itemsArray.map((item) => sanitizeCartAddItem(item));

    if (this.allowDebug()) {
      console.log("CartStateManager: Adding items to cart", {
        items: itemsArray,
        itemCount: itemsArray.length,
        openDrawer: openDrawer,
        currentItemCount: this.state.itemCount,
      });
    }

    this.isUpdating = true;

    try {
      this._publish(EVENTS.CART_STATE_ITEM_ADDING, { items: itemsArray, openDrawer });

      // Build request body with bundled cart-json section for synchronized data
      const baseRequestBody =
        requestItems.length === 1 ? requestItems[0] : { items: requestItems };
      const requestBody = {
        ...baseRequestBody,
        sections: "cart-json"
      };

      const response = await fetch(`${window.theme?.routes?.cart_add_url}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add items to cart");
      }

      const responseData = await response.json();

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Items added successfully, response received",
          {
            hasCartData: !!responseData,
            responseKeys: Object.keys(responseData),
            hasSections: !!responseData.sections,
            responseDataStructure: responseData
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);

          if (this.allowDebug()) {
            console.log("CartStateManager: Enriched data parsed from bundled addItems response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      } else {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after addItems (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with the enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh data available
      this.#dispatchStateChangeEvent(enrichedCartData, { source: 'addItems' });

      const addedLineItems = this.#resolveAddedLineItems(
        responseData,
        previousItems,
        this.state.items,
        itemsArray
      );

      this._publish(EVENTS.CART_STATE_ITEM_ADDED, {
        items: itemsArray,
        openDrawer,
        state: this.state,
        addedLineItems,
        addResponse: responseData,
      });

      if (this.allowDebug()) {
        console.log("CartStateManager: Add items complete", {
          newItemCount: this.state.itemCount,
          itemsAdded: itemsArray.length,
          addedLineItemsCount: addedLineItems.length,
        });
      }

      return {
        success: true,
        items: itemsArray,
        addedLineItems,
        addResponse: responseData,
      };
    } catch (error) {
      console.error("CartStateManager: Error adding items to cart:", error);
      this._publish(EVENTS.CART_STATE_ERROR, { error, operation: "add", items: itemsArray });
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Resolve canonical added line items from add.js response.
   * Falls back to cart state diffing when Shopify responds with sections-only payloads.
   * @param {Object|null} responseData
   * @param {Array} previousItems
   * @param {Array} currentItems
   * @param {Array} requestedItems
   * @returns {Array}
   * @private
   */
  #resolveAddedLineItems(responseData, previousItems, currentItems, requestedItems) {
    if (Array.isArray(responseData?.items) && responseData.items.length > 0) {
      return responseData.items;
    }

    if (responseData?.id && responseData?.key) {
      return [responseData];
    }

    const previousKeys = new Set(
      (Array.isArray(previousItems) ? previousItems : [])
        .map((item) => item?.key)
        .filter(Boolean)
    );
    const currentItemsArray = Array.isArray(currentItems) ? currentItems : [];
    const addedByNewKey = currentItemsArray.filter(
      (item) => item?.key && !previousKeys.has(item.key)
    );

    if (addedByNewKey.length > 0) {
      return addedByNewKey;
    }

    const requestedVariantIds = new Set(
      (Array.isArray(requestedItems) ? requestedItems : [])
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id))
    );

    if (requestedVariantIds.size === 0) {
      return [];
    }

    const previousQtyByVariantId = new Map();
    for (const item of Array.isArray(previousItems) ? previousItems : []) {
      const variantId = Number(item?.variantId || item?.id);
      if (!Number.isFinite(variantId)) continue;
      const quantity = Number(item?.quantity) || 0;
      previousQtyByVariantId.set(
        variantId,
        (previousQtyByVariantId.get(variantId) || 0) + quantity
      );
    }

    const addedByQuantity = [];
    for (const item of currentItemsArray) {
      const variantId = Number(item?.variantId || item?.id);
      if (!Number.isFinite(variantId) || !requestedVariantIds.has(variantId)) {
        continue;
      }
      const previousQuantity = previousQtyByVariantId.get(variantId) || 0;
      const currentQuantity = Number(item?.quantity) || 0;
      if (currentQuantity > previousQuantity) {
        addedByQuantity.push(item);
      }
    }

    return addedByQuantity;
  }

  /**
   * Update item quantity in cart (proxy to updateCart)
   * @param {string} key - Cart item key
   * @param {number} quantity - New quantity
   */
  async updateItem(key, quantity) {
    return this.updateCart({ updates: { [key]: quantity } });
  }

  /**
   * Update multiple items quantities in cart (proxy to updateCart)
   * @param {Object} updates - Object mapping line item keys to quantities
   */
  async updateItems(updates) {
    return this.updateCart({ updates });
  }

  /**
   * Comprehensive cart update using update.js endpoint
   * @param {Object} options - Update options
   * @param {Object} options.updates - Line item quantity updates { key: quantity }
   * @param {string} options.note - Cart note
   * @param {Object} options.attributes - Cart attributes { key: value }
   * @param {string} options.discount - Discount code(s), comma-separated for multiple
   */
  async updateCart(options = {}) {
    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Update cart blocked - already updating");
      }
      return;
    }

    const { updates, note, attributes, discount } = options;

    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart", {
        updates: updates,
        note: note,
        attributes: attributes,
        discount: discount,
        updateCount: updates ? Object.keys(updates).length : 0,
        currentItemCount: this.state.itemCount,
      });
    }

    this.isUpdating = true;

    try {
      this._publish(EVENTS.CART_STATE_CART_UPDATING, { updates, note, attributes, discount });

      // Build request body with bundled cart-json section for synchronized data
      const requestBody = {
        sections: "cart-json"
      };
      if (updates) requestBody.updates = updates;
      if (note !== undefined) requestBody.note = note;
      if (attributes) requestBody.attributes = attributes;
      if (discount !== undefined) requestBody.discount = discount;

      const response = await fetch(
        `${window.theme?.routes?.cart_update_url}.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update cart");
      }

      const responseData = await response.json();

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Cart updated successfully",
          {
            hasResponseData: !!responseData,
            hasSections: !!responseData.sections
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);
          if (this.allowDebug()) {
            console.log("CartStateManager: Parsed enriched cart data from bundled updateCart response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      }

      // Fall back to fetching if bundled parsing failed
      if (!enrichedCartData) {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after updateCart (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh enriched data available
      this.#dispatchStateChangeEvent(enrichedCartData, { source: 'updateCart' });

      // Dispatch specific events based on what was updated
      if (updates && Object.keys(updates).length > 0) {
        this._publish(EVENTS.CART_STATE_ITEM_UPDATED, { updates, state: this.state });
      }

      if (note !== undefined) {
        this._publish(EVENTS.CART_STATE_NOTE_UPDATED, { note, state: this.state });
      }

      if (attributes) {
        this._publish(EVENTS.CART_STATE_ATTRIBUTES_UPDATED, { attributes, state: this.state });
      }

      if (discount !== undefined) {
        this._publish(EVENTS.CART_STATE_DISCOUNT_UPDATED, { discount, state: this.state });
      }

      this._publish(EVENTS.CART_STATE_CART_UPDATED, { updates, note, attributes, discount, state: this.state });

      if (this.allowDebug()) {
        console.log("CartStateManager: Update cart complete", {
          newItemCount: this.state.itemCount,
          updatedItems: updates ? Object.keys(updates).length : 0,
          noteUpdated: note !== undefined,
          attributesUpdated: !!attributes,
          discountUpdated: discount !== undefined,
        });
      }

      return { success: true, updates, note, attributes, discount };
    } catch (error) {
      console.error("CartStateManager: Error updating cart:", error);
      this._publish(EVENTS.CART_STATE_ERROR, { error, operation: "update", updates, note, attributes, discount });
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Change a single item using change.js endpoint (quantity, properties, selling_plan)
   * @param {string} key - Cart item key
   * @param {Object} changes - Changes to apply (quantity, properties, selling_plan)
   */
  async changeItem(key, changes) {
    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log("CartStateManager: Change item blocked - already updating");
      }
      return;
    }

    if (this.allowDebug()) {
      const currentItem = this.getItem(key);
      console.log("CartStateManager: Changing item in cart", {
        key: key,
        changes: changes,
        currentQuantity: currentItem?.quantity || 0,
        productTitle: currentItem?.productTitle || "Unknown",
      });
    }

    this.isUpdating = true;

    try {
      this._publish(EVENTS.CART_STATE_ITEM_CHANGING, { key, changes });

      const requestBody = { id: key, ...changes }; // No bundled sections since we fetch fresh enriched data separately

      const response = await fetch(
        `${window.theme?.routes?.cart_change_url}.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to change item");
      }

      const responseData = await response.json();

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Item changed successfully"
        );
      }

      // Fetch fresh enriched cart data (changeItem doesn't support bundled sections)
      let enrichedCartData = null;
      try {
        enrichedCartData = await this.#fetchEnrichedCartData();
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetched enriched cart data after changeItem operation");
        }
      } catch (error) {
        console.error("CartStateManager: Failed to fetch enriched cart data:", error);
        throw error;
      }

      // Update state with enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh enriched data available
      this.#dispatchStateChangeEvent(enrichedCartData, { source: 'changeItem' });

      this._publish(EVENTS.CART_STATE_ITEM_CHANGED, { key, changes, state: this.state });

      if (this.allowDebug()) {
        console.log("CartStateManager: Change item complete", {
          newItemCount: this.state.itemCount,
        });
      }
    } catch (error) {
      console.error("CartStateManager: Error changing item in cart:", error);
      this._publish(EVENTS.CART_STATE_ERROR, { error, operation: "change", key, changes });
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Remove item from cart (proxy to removeItems)
   * @param {string} key - Cart item key
   */
  async removeItem(key) {
    return this.removeItems([key]);
  }

  /**
   * Remove multiple items from cart using update.js endpoint
   * @param {Array<string>} keys - Array of cart item keys to remove
   */
  async removeItems(keys) {
    if (this.isUpdating) {
      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Remove items blocked - already updating"
        );
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("CartStateManager: Removing multiple items from cart", {
        keys: keys,
        itemCount: keys.length,
      });
    }

    this.isUpdating = true;

    try {
      // Get items before removal for the event
      const itemsBeingRemoved = keys
        .map((key) => this.getItem(key))
        .filter(Boolean);

      this._publish(EVENTS.CART_STATE_ITEM_REMOVING, { keys, items: itemsBeingRemoved });

      // Use update.js endpoint to set quantities to 0 with bundled section rendering
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });

      const response = await fetch(
        `${window.theme?.routes?.cart_update_url}.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates, sections: "cart-json" }), // Bundle cart-json section for synchronized data
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to remove items");
      }

      const responseData = await response.json();

      if (this.allowDebug()) {
        console.log(
          "CartStateManager: Items removed successfully",
          {
            hasResponseData: !!responseData,
            hasSections: !!responseData.sections
          }
        );
      }

      // Parse enriched cart data from bundled response
      let enrichedCartData = null;

      if (responseData.sections && responseData.sections["cart-json"]) {
        try {
          enrichedCartData = this.#parseEnrichedCartData(responseData.sections);
          if (this.allowDebug()) {
            console.log("CartStateManager: Parsed enriched cart data from bundled removeItems response");
          }
        } catch (error) {
          console.warn("CartStateManager: Failed to parse bundled enriched cart data:", error);
        }
      }

      // Fall back to fetching if bundled parsing failed
      if (!enrichedCartData) {
        if (this.allowDebug()) {
          console.log("CartStateManager: Fetching enriched cart data after removeItems (fallback)");
        }
        try {
          enrichedCartData = await this.#fetchEnrichedCartData();
        } catch (error) {
          console.error("CartStateManager: Failed to fetch enriched cart data:", error);
          throw error;
        }
      }

      // Update state with enriched cart data (but don't dispatch events yet)
      this._updateState(enrichedCartData, { suppressEvents: true });

      // Now dispatch the state change event with fresh enriched data available
      this.#dispatchStateChangeEvent(enrichedCartData, { source: 'removeItems' });

      this._publish(EVENTS.CART_STATE_ITEM_REMOVED, { keys, items: itemsBeingRemoved, state: this.state });

      if (this.allowDebug()) {
        console.log("CartStateManager: Remove items complete", {
          newItemCount: this.state.itemCount,
          removedItems: keys.length,
        });
      }

      return { success: true, removedItems: itemsBeingRemoved };
    } catch (error) {
      console.error("CartStateManager: Error removing items from cart:", error);
      this._publish(EVENTS.CART_STATE_ERROR, { error, operation: "remove", keys });
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Get current cart state
   * @returns {Object} Current cart state
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Get specific cart item by key
   * @param {string} key - Cart item key
   * @returns {Object|null} Cart item or null if not found
   */
  getItem(key) {
    return this.state.items.find((item) => item.key === key) || null;
  }

  /**
   * Get all cart items
   * @returns {Array} Array of cart items
   */
  getItems() {
    return [...this.state.items];
  }

  /**
   * Check if cart is empty
   * @returns {boolean} True if cart is empty
   */
  isEmpty() {
    return this.state.itemCount === 0;
  }

  /**
   * Get formatted total price
   * @returns {string} Formatted total price
   */
  getFormattedTotal() {
    return this.state.totalPriceMoney;
  }

  /**
   * Update cart attributes (convenience method)
   * @param {Object} attributes - Cart attributes to update
   */
  async updateAttributes(attributes) {
    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart attributes", {
        attributes: attributes,
        currentAttributes: this.state.attributes,
      });
    }

    return this.updateCart({ attributes });
  }

  /**
   * Update cart note (convenience method)
   * @param {string} note - New cart note
   */
  async updateNote(note) {
    if (this.allowDebug()) {
      console.log("CartStateManager: Updating cart note", {
        note: note,
        currentNote: this.state.note,
      });
    }

    return this.updateCart({ note });
  }

  /**
   * Apply discount code(s) to cart (convenience method)
   * @param {string|Array<string>} discounts - Discount code(s) to apply
   */
  async applyDiscount(discounts) {
    const discountString = Array.isArray(discounts)
      ? discounts.join(",")
      : discounts;

    if (this.allowDebug()) {
      console.log("CartStateManager: Applying discount codes", {
        discounts: discountString,
        currentDiscounts: this.state.cartLevelDiscountApplications,
      });
    }

    return this.updateCart({ discount: discountString });
  }

  /**
   * Remove all discount codes from cart (convenience method)
   */
  async removeDiscounts() {
    if (this.allowDebug()) {
      console.log("CartStateManager: Removing all discount codes");
    }

    return this.updateCart({ discount: "" });
  }

  /**
   * Parse enriched cart data from bundled section response
   * @param {Object} sections - Sections object from cart API response
   * @returns {Object|null} Parsed enriched cart data or null if parsing failed
   * @private
   */
  #parseEnrichedCartData(sections) {
    if (!sections || !sections["cart-json"]) {
      if (this.allowDebug()) {
        console.log("CartStateManager: No cart-json section in bundled response", {
          sections: sections,
          availableSections: sections ? Object.keys(sections) : []
        });
      }
      return null;
    }

    try {
      const cartDataContent = sections["cart-json"];

      if (this.allowDebug()) {
        console.log("CartStateManager: Parsing cart-json section", {
          contentLength: cartDataContent ? cartDataContent.length : 0,
          contentPreview: cartDataContent ? cartDataContent.substring(0, 200) : null
        });
      }

      // Parse the cart data from the script tag wrapper
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartDataContent, "text/html");
      const scriptTag = doc.querySelector("#cart__json");

      if (!scriptTag) {
        console.error("CartStateManager: Script tag #cart__json not found in cart-json section");
        return null;
      }

      const enrichedCartData = JSON.parse(scriptTag.textContent);

      if (this.allowDebug()) {
        console.log("CartStateManager: Enriched cart data parsed from bundled response", {
          renderCartNote: enrichedCartData.renderCartNote,
          itemCount: enrichedCartData.itemCount,
          hasItems: enrichedCartData.items?.length || 0,
          hasCompareAtPriceTotals: !!enrichedCartData.compareAtPriceTotals
        });
      }

      return enrichedCartData;
    } catch (error) {
      console.error("CartStateManager: Error parsing enriched cart data from bundled response:", error);
      return null;
    }
  }

  /**
   * Fetch enriched cart data from cart-json section
   * @returns {Promise<Object>} Enriched cart data
   * @private
   */
  async #fetchEnrichedCartData() {
    if (this.allowDebug()) {
      console.log("CartStateManager: Fetching enriched cart data from cart-json section");
    }

    try {
      const response = await fetch(`${window.theme?.routes?.root_url}?sections=cart-json`);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch cart-json section: ${response.status}`
        );
      }

      const data = await response.json();
      const enrichedCartData = this.#parseEnrichedCartData(data);

      if (!enrichedCartData) {
        throw new Error("Failed to parse enriched cart data");
      }

      return enrichedCartData;
    } catch (error) {
      console.error("CartStateManager: Error fetching enriched cart data:", error);
      throw error;
    }

  }

  /**
   * Update all [data-cart-count] elements in the DOM
   * @param {number|null} itemCount - Item count to display (defaults to current state)
   */
  updateAllCartCountElements(itemCount = null) {
    const count = itemCount ?? this.state.itemCount;
    const cartCountElements = document.querySelectorAll('[data-cart-count]');

    cartCountElements.forEach(element => {
      element.textContent = count;
      element.classList.toggle('hidden', count === 0);
    });

    if (this.allowDebug()) {
      console.log(`CartStateManager: Updated ${cartCountElements.length} cart count elements`, { count });
    }
  }
}

// Auto-initialize and set global reference when imported
let cartStateManagerInstance = null;

/**
 * Initialize the cart state manager
 * @returns {Promise<CartStateManager>} The initialized cart state manager instance
 */
export async function initializeCartStateManager() {
  if (cartStateManagerInstance) return cartStateManagerInstance;

  cartStateManagerInstance = new CartStateManager();
  await cartStateManagerInstance.initialize();

  return cartStateManagerInstance;
}

/**
 * Get the current cart state manager instance
 * @returns {CartStateManager|null} The cart state manager instance or null if not initialized
 */
export function getCartStateManager() {
  return cartStateManagerInstance;
}

// Auto-initialize for backward compatibility when not using ES6 modules
(() => {
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      try {
        await initializeCartStateManager();
      } catch (error) {
        console.error('CartStateManager: Auto-initialization failed:', error);
      }
    });
  } else {
    // DOM is already ready
    initializeCartStateManager().then(() => {
    }).catch(error => {
      console.error('CartStateManager: Auto-initialization failed:', error);
    });
  }
})();

export default CartStateManager;
